/* @flow */
/* eslint react/no-direct-mutation-state: off */
import React, { Component } from 'react'
import { Map } from 'immutable'
import chooseStep from './choose_step'
import CurrentStep from './current_step'
import { urlParams, objectToParams } from '../helpers/url'
import i18n from '../helpers/i18n'
import { toCamelCase } from '../helpers/string'
import CancelablePromise from '../cancelable_promise'
import DataHandler from '../data_handler'

import type { StepListType } from './current_step'
import type { DataType } from '../data_handler'
import type { ApiError } from '../api'

type PopStateEventType = Event & { state: Object }
export type ChangeStepType = (step: string) => CancelablePromise

export type WizardPropsType = {
  locale: string,
  data: DataHandler
}

export type WizardStateType = {
  loading: boolean,
  otp: boolean,
  otpData: ?Object,
  error: ?string,
  otpReload: boolean,
  previousStep: ?string
}

export type IdType = string | number
export type SendOtpType = (IdType, boolean) => CancelablePromise
export type VerifyOtpType = (string) => CancelablePromise
export type UpdateDataType = (data: DataType, customStep?: string) => CancelablePromise
export type RenderOptionType = {
  currentStep: CurrentStep,
  changeStep: ChangeStepType,
  updateData: UpdateDataType,
  data: DataHandler,
  locale: string,
  id: IdType,
  sendOtp: SendOtpType,
  verifyOtp: VerifyOtpType,
  errorMessage?: string,
  otpData?: DataType,
  previousStep: ?string
}

/**
 * Wizard
 */
class Wizard<Props: WizardPropsType, State: WizardStateType> extends Component<Props, State> {
  static urlParams: Array<string> = ['hash', 'search']

  // possible values are id, step, locale otherwise they have to be added in historyUrlObject
  static usedUrlParams: { hash: Array<string>, search: Array<string> } = {
    hash: [],
    search: ['id']
  }

  static +otpStep: React$Component<any, any>
  static +loadingStep: React$Component<any, any>
  static +errorStep: React$Component<any, any>
  static stepName: string = 'step'

  previousStepComponent: ?React$Component<*>
  previousStepOptions: ?RenderOptionType

  // $FlowFixMe
  state: State = {
    loading: false,
    otp: false,
    otpReload: false,
    otpData: null,
    error: null,
    previousStep: null
  }

  static getDerivedStateFromError(error: Object) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error }
  }

  componentDidMount() {
    window.addEventListener('popstate', this.registerPopState)
    const { data } = this.props
    if (!data.get(this.constructor.stepName)) data.set(this.constructor.stepName, this.steps(data)[0].name)
    data.component(this)
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.registerPopState)
    const { data } = this.props

    data.component(null)
  }

  get id(): IdType {
    return (this.props.data.get('id'): any)
  }

  get step(): string {
    return (this.props.data.get(this.constructor.stepName): any)
  }

  registerPopState = (event: PopStateEventType) => {
    const { state }: { state: Object } = event
    const currentId = this.id
    if (state) {
      const { step, id, locale } = state
      if (step) this.changeStep(step)
      if (id != currentId) this.loadData(id)
      if (locale) {
        i18n.locale = locale
        this.forceUpdate()
      }
    }
  }

  loadData = (id: IdType): CancelablePromise => {
    return this.loadDataPromise(id).then(this.loadDataResolveHandler(id), this.loadDataReject(id))
  }

  loadDataPromise(_id: IdType): CancelablePromise {
    throw 'overwrite me'
  }

  loadDataResolveHandler = (id: IdType, customStep?: string) => (serverData: DataType): CancelablePromise => {
    const params = this.getUrlParams()
    return this.loadDataResolve(id, serverData, params, customStep)
  }

  loadDataResolve(_id: IdType, serverData: DataType, params: Object, customStep?: string): CancelablePromise {
    i18n.locale = params.locale || ((serverData.get('locale'): any): string)
    const promise = this.props.data.update((updateData) => {
      const data = updateData.merge(serverData)
      return data.set(this.constructor.stepName, this.correctStep(data, params, customStep))
    })
    this.setState({
      loading: false,
      otp: false,
      error: null
    })
    return promise.then((data) => {
      this.setHistoryEntry()
      return data
    })
  }

  loadDataReject = (id: IdType, data?: DataType) => (error: ApiError) => {
    if (error && error.response && error.response.status == 401) {
      // $FlowFixMe
      if (data) this.props.data.update(() => data)
      return this.unauthorized(id)
    }
    // console.error(error)
    return CancelablePromise.reject(error)
  }

  updateDataPromise(_id: IdType, _serverData: DataType, _customStep?: string): CancelablePromise {
    throw 'overwrite me'
  }

  updateData = (data: DataType, customStep?: string): CancelablePromise => {
    const id = this.id
    return this.updateDataPromise(id, data, customStep).then(
      this.loadDataResolveHandler(id, customStep),
      this.loadDataReject(id, data)
    )
  }

  unauthorized(id: IdType) {
    return this.sendOtp(id, true)
  }

  // usually overriden in specific implementation.
  // data, _params and _customStep are useful for deciding
  // which step to take next.
  correctStep(data: DataType, _params: Object, _customStep?: string): string {
    const s = ((data.get(this.constructor.stepName): any): string)
    const step = toCamelCase(s)
    return chooseStep(this.steps(data), step).nextStep.name
  }

  get currentStep(): CurrentStep {
    return chooseStep(this.steps(this.props.data), this.step)
  }

  // $FlowFixMe
  steps(_data = this.props.data): StepListType {
    throw 'overwrite method "steps"'
  }

  stepComponent(): React$Component<*> {
    const { error, otp, loading } = this.state
    if (this.constructor.errorStep && error != null) {
      return this.constructor.errorStep
    } else if (this.constructor.otpStep && otp === true) {
      return this.constructor.otpStep
    } else if (this.constructor.loadingStep && loading === true) {
      return this.constructor.loadingStep
    } else {
      return this.currentStep.stepComponent
    }
  }

  canChangeStep(): boolean {
    return true
  }

  changeStep = (step: string, callback?: () => any) => {
    if (this.canChangeStep()) {
      const { data } = this.props
      this.setState({ previousStep: this.step })
      return data.set(this.constructor.stepName, step).then(() => {
        this.changeStepScrollTo()
        this.setHistoryEntry()
        if (typeof callback == 'function') callback()
      })
    }
    return CancelablePromise.resolve(this.currentStep.name)
  }

  changeStepScrollTo() {
    window.scrollTo(0, 0)
  }

  historyUrlObject() {
    const { data } = this.props
    return {
      step: this.step,
      id: this.id,
      locale: data.get('locale')
    }
  }

  getUrlParams() {
    const obj = {}
    this.constructor.urlParams.forEach((type: string) => {
      Object.assign(obj, urlParams(window.location[type].substr(1)))
    })
    return obj
  }

  updateUrlParams(type: string, object: Object): Object {
    const keys = this.constructor.usedUrlParams[type]
    const params = keys.reduce((obj, key) => {
      obj[key] = object[key]
      return obj
    }, {})
    return Object.assign(urlParams(window.location[type].substr(1)), params)
  }

  updateSearchParams(params: Object) {
    return objectToParams(params)
  }

  updateHashParams(params: Object) {
    return objectToParams(params)
  }

  setHistoryEntry() {
    const titleElement: ?HTMLElement = document.querySelector('title')
    const title: string = titleElement ? titleElement.innerText || '' : ''
    const object = this.historyUrlObject()
    const pathname = window.location.pathname
    const search = this.updateSearchParams(this.updateUrlParams('search', object))
    const hash = this.updateHashParams(this.updateUrlParams('hash', object))
    const href = pathname + (search ? `?${search}` : '') + (hash ? `#${hash}` : '')
    history.pushState(object, title, href)
  }

  sendOtp = (id: IdType, otpReload: boolean = true): CancelablePromise => {
    this.setState({ loading: true, otp: true, otpReload })
    this.props.data.update((d) => d.merge(Map({ id })))
    return this.sendOtpPromise(id).then(this.sendOtpResolve, this.sendOtpReject)
  }

  sendOtpPromise(_id: IdType): CancelablePromise {
    throw 'overwrite me'
  }

  sendOtpResolve = (otpData: DataType): CancelablePromise => {
    return new CancelablePromise((resolve) => {
      this.setState(
        {
          otpData: otpData,
          loading: false
        },
        resolve
      )
    })
  }

  sendOtpReject = (error: ApiError): ApiError => {
    console.error(error)
    return CancelablePromise.reject(error)
  }

  verifyOtp = (pin: string): CancelablePromise => {
    const { data } = this.props
    return this.verifyOtpPromise((data.getIn(['id']): any), pin).then(this.verifyOtpResolve, this.verifyOtpReject)
  }

  verifyOtpPromise(_id: IdType, _pin: string): CancelablePromise {
    throw 'overwrite me'
  }

  verifyOtpResolve = (): CancelablePromise => {
    const { otpReload } = this.state
    this.setState({ otp: false, loading: otpReload, otpData: null })

    if (otpReload) return this.loadData(this.id)
    return CancelablePromise.resolve()
  }

  verifyOtpReject = (error: ApiError): CancelablePromise => {
    let status
    if (error && error.response && error.response.status == 429) {
      status = 'tooManyRequests'
    } else {
      status = 'failed'
    }
    this.setState((state) => {
      return { otpData: { ...state.otpData, status: status } }
    })
    return CancelablePromise.reject(error)
  }

  renderOptions(step: React$Component<*>): RenderOptionType {
    const { data } = this.props
    const { error, otpData, previousStep } = this.state
    const options: RenderOptionType = {
      currentStep: this.currentStep,
      data: data.prefix([]),
      locale: (data.get('locale'): any),
      id: this.id,
      previousStep,
      sendOtp: this.sendOtp,
      verifyOtp: this.verifyOtp,
      changeStep: this.changeStep,
      updateData: this.updateData,
      loadData: this.loadData
    }
    if (step == this.constructor.errorStep) {
      if (error) options.errorMessage = error
    } else if (step == this.constructor.otpStep) {
      if (otpData) options.otpData = otpData
    }

    return options
  }

  // $FlowFixMe
  render(): ?React$Element<*> {
    const Step = this.stepComponent()
    const options = this.renderOptions(Step)
    this.previousStepComponent = Step
    this.previousStepOptions = options
    // $FlowFixMe
    return <Step {...options} />
  }
}

export default Wizard
